// The console chrome. One component so no page can build its own nav.
//
//   below 640    AppShell: pinned rail header, Material navigation bar at
//                the bottom edge (4 destinations plus More, which holds the
//                other 2), contained scroll
//   640 to 1023  AppShell: the same destinations in the header row
//   1024 and up  the navigation drawer (components.md "Navigation rail and
//                drawer"): 256 wide, layer-1, separated from the page by
//                that tonal step alone; AppShell's header (and with it the
//                Primary nav and More) is display: none, so exactly one
//                navigation is visible at any width
//
// The six destinations are the same at every width. A destination may carry
// a shortLabel for the compact chrome below 1024 (the bottom bar gives each
// item a fifth of a 320px phone); the drawer always shows the full label.
//
// Children render inside .con-page, which owns the page padding, the 1280
// cap, and the one vertical rhythm between regions, so a page never sets its
// own outer spacing. `fill` makes .con-page a viewport-high column so one
// region (the AI chat) can take the rest of the height and scroll inside.
import type { ReactElement, ReactNode } from "react";
import { MotionConfig } from "motion/react";
import { AppShell, type AppShellDestination } from "../AppShell";
import { ConsoleGlyph, type ConsoleGlyphName } from "./icons";

export type ConsoleDensity = "compact" | "default" | "comfortable";

export interface ConsoleDestination {
  /** Stable id, matched against `current`. */
  id: string;
  /** Full label, shown in the drawer (1024 and up). */
  label: string;
  /** Optional shorter label for the header and bottom bar below 1024. */
  shortLabel?: string;
  href: string;
  /** A console glyph name, or a <ConsoleGlyph /> element. */
  icon: ConsoleGlyphName | ReactElement;
  /** A count shown as a flat badge, named on the link ("Insight, 3 baru"). */
  badge?: number;
}

export interface ConsoleShellProps {
  /** Product name: the header wordmark below 1024 and the drawer title from 1024. */
  title: string;
  /** One supporting line under the drawer title, e.g. the active periode. */
  meta?: string;
  /**
   * A control under the drawer title (1024 and up) and in the header actions
   * below 1024, e.g. a switch to another dashboard. Replaces `meta` in the
   * drawer when both are given.
   */
  switcher?: ReactNode;
  destinations: ConsoleDestination[];
  /** Id of the current destination; gets aria-current="page" in every nav. */
  current: string;
  /** Desktop table and list density, set by JEV ui.density. Default "default". */
  density?: ConsoleDensity;
  /** Make the page a viewport-high column for one filling region (chat). */
  fill?: boolean;
  /** Accessible name of the navigation landmarks. */
  navLabel?: string;
  /** Skip link text (1024 and up; AppShell owns its own below 1024). */
  skipLabel?: string;
  /** Id of the main region, the skip link target. */
  mainId?: string;
  children: ReactNode;
}

function glyph(icon: ConsoleDestination["icon"], size: 20 | 24): ReactElement {
  return typeof icon === "string" ? <ConsoleGlyph name={icon} size={size} /> : icon;
}

function countLabel(n: number | undefined): string {
  return typeof n === "number" && n > 0 ? `, ${n} baru` : "";
}

export function ConsoleShell({
  title,
  meta,
  switcher,
  destinations,
  current,
  density = "default",
  fill = false,
  navLabel = "Navigasi utama",
  skipLabel = "Langsung ke konten",
  mainId = "main",
  children,
}: ConsoleShellProps) {
  const compact: AppShellDestination[] = destinations.map((d) => ({
    id: d.id,
    label: d.shortLabel ?? d.label,
    href: d.href,
    icon: glyph(d.icon, 24),
    badge: d.badge,
  }));

  return (
    <MotionConfig reducedMotion="user">
      <div className="con-shell" data-density={density}>
        <a className="shell-skip con-skip" href={`#${mainId}`}>
          {skipLabel}
        </a>
        <div className="con-drawer">
          <div className="con-drawer-head">
            <p className="con-drawer-title">{title}</p>
            {switcher ? switcher : meta ? <p className="con-drawer-meta">{meta}</p> : null}
          </div>
          <nav className="con-drawer-nav" aria-label={navLabel}>
            <ul className="con-drawer-list">
              {destinations.map((d) => {
                const active = d.id === current;
                return (
                  <li key={d.id}>
                    <a
                      className="con-drawer-item"
                      href={d.href}
                      aria-current={active ? "page" : undefined}
                      aria-label={d.badge ? `${d.label}${countLabel(d.badge)}` : undefined}
                    >
                      <span className="con-drawer-icon">{glyph(d.icon, 20)}</span>
                      <span className="con-drawer-label">{d.label}</span>
                      {typeof d.badge === "number" && d.badge > 0 ? (
                        <span className="con-drawer-count" aria-hidden="true">
                          {d.badge > 99 ? "99+" : d.badge}
                        </span>
                      ) : null}
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
        <div className="con-body">
          <AppShell
            title={title}
            destinations={compact}
            current={current}
            actions={switcher}
            scroll="contained"
            archetype={{ header: "rail", bar: "bar", labels: "all" }}
            moreIcon={<ConsoleGlyph name="more" size={24} />}
            mainId={mainId}
            spy={false}
          >
            <div className="con-page" data-fill={fill ? "" : undefined}>
              {children}
            </div>
          </AppShell>
        </div>
      </div>
    </MotionConfig>
  );
}
